import React, { useState } from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

import { BugDetails } from '../../../../components/Cycles/Bugs/BugDetails';
import { getApprovedDevicePrettyName } from '../../../../services/deviceService';
import { CycleStatus } from '../../../../types/CycleStatus';
import useIndicators from '../../../../util/useIndicators';
import { BugStatusChip, ConfirmDialog, CountChip, SeverityChip } from '../../../../ui';
import { colors } from '../../../../theme/tokens';

interface Props {
    bugs: any[];
    cycle: any;
    isMobile?: boolean;
    onEdit?: (id: number) => void;
    onDelete?: (id: number) => void;
}

const headCell = { fontSize: 11, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' as const, color: colors.textGrey, borderColor: colors.dividerBlue };

/* The tester's own bugs in a cycle. Edit/Delete are passed only while the tester may still change
   bugs, and are disabled once the cycle and its service time are over. */
const TesterBugsList: React.FC<Props> = ({ bugs, cycle, isMobile = false, onEdit, onDelete }) => {
    const [openBugId, setOpenBugId] = useState<number | null>(null);
    const [deleteBug, setDeleteBug] = useState<any>(null);
    const { indicators } = useIndicators(true);

    const isCycleOver = cycle.status === CycleStatus.Completed && moment(cycle.questionsTime).isBefore(moment());
    const openBug = bugs.find((bug) => bug.id === openBugId);

    // One open indicator per unseen comment
    const unreadComments = (bug) =>
        bug.chatId ? indicators.filter((indicator) => indicator.entityType === 'chat' && indicator.entityRef === bug.chatId).length : 0;

    const actions = (bug) =>
        (onEdit || onDelete) && (
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }} onClick={(event) => event.stopPropagation()}>
                {onEdit && (
                    <Button size="small" disabled={isCycleOver} onClick={() => onEdit(bug.id)}>
                        Edit
                    </Button>
                )}
                {onDelete && (
                    <Button size="small" color="error" disabled={isCycleOver} onClick={() => setDeleteBug(bug)}>
                        Delete
                    </Button>
                )}
            </Box>
        );

    const title = (bug) => (
        <>
            {bug.topic}
            {unreadComments(bug) > 0 && (
                <CountChip unread value={`${unreadComments(bug)} new`} title={`${unreadComments(bug)} unread comment${unreadComments(bug) === 1 ? '' : 's'}`} />
            )}
        </>
    );

    return (
        <>
            {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {bugs.map((bug) => (
                        <Box
                            key={bug.id}
                            onClick={() => setOpenBugId(bug.id)}
                            sx={{ border: `1px solid ${colors.dividerBlue}`, borderRadius: 2, p: 1.5, cursor: 'pointer', backgroundColor: '#fff' }}
                        >
                            <Typography sx={{ fontSize: 11, color: colors.textGrey, fontFamily: 'monospace' }}>#{bug.publicId}</Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 500, my: 0.5 }}>{title(bug)}</Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                                <SeverityChip severity={bug.severity} />
                                <BugStatusChip status={bug.status} />
                                <Box sx={{ flex: 1 }} />
                                {actions(bug)}
                            </Box>
                        </Box>
                    ))}
                </Box>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headCell}>ID</TableCell>
                            <TableCell sx={headCell}>Title</TableCell>
                            <TableCell sx={headCell}>Step</TableCell>
                            <TableCell sx={headCell}>Severity</TableCell>
                            <TableCell sx={headCell}>Status</TableCell>
                            <TableCell sx={headCell} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bugs.map((bug) => (
                            <TableRow key={bug.id} hover onClick={() => setOpenBugId(bug.id)} sx={{ cursor: 'pointer', '& td': { borderColor: '#f0f3f8' } }}>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: colors.textGrey, whiteSpace: 'nowrap' }}>{bug.publicId}</TableCell>
                                <TableCell sx={{ fontSize: 14 }}>{title(bug)}</TableCell>
                                <TableCell sx={{ fontSize: 13, color: colors.textGrey }}>{bug.testPlanStepId || '—'}</TableCell>
                                <TableCell><SeverityChip severity={bug.severity} /></TableCell>
                                <TableCell><BugStatusChip status={bug.status} /></TableCell>
                                <TableCell>{actions(bug)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Drawer
                anchor="right"
                open={!!openBug}
                onClose={() => setOpenBugId(null)}
                PaperProps={{ sx: { width: isMobile ? '100%' : 'clamp(560px, 50vw, 960px)', p: isMobile ? 2 : 4 } }}
            >
                {openBug && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, color: colors.textGrey, fontFamily: 'monospace' }}>#{openBug.publicId}</Typography>
                                <Typography component="h2" sx={{ fontSize: 22, fontWeight: 600, color: colors.titleBlue, lineHeight: 1.3 }}>{openBug.topic}</Typography>
                            </Box>
                            <IconButton aria-label="Close" onClick={() => setOpenBugId(null)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.75, mb: 2 }}>
                            <SeverityChip severity={openBug.severity} />
                            <BugStatusChip status={openBug.status} />
                        </Box>
                        <BugDetails
                            bugId={openBug.id}
                            chatId={openBug.chatId}
                            refreshKey={openBug.chatId ? indicators.filter((indicator) => indicator.entityType === 'chat' && indicator.entityRef === openBug.chatId).map((indicator) => indicator.id).join(',') : ''}
                            step={openBug.testPlanStepId}
                            deviceName={getApprovedDevicePrettyName(openBug.device)}
                            stepsToReproduce={openBug.stepsToReproduce}
                            expectedResult={openBug.expectedResult}
                            actualResult={openBug.actualResult}
                            comment={openBug.comment}
                            imageFilePaths={openBug.imageFilePaths}
                            videoFilePaths={openBug.videoFilePaths}
                            createdAt={openBug.createdAt}
                        />
                    </>
                )}
            </Drawer>

            <ConfirmDialog
                open={!!deleteBug}
                title="Delete this bug?"
                message={deleteBug && `#${deleteBug.publicId} "${deleteBug.topic}" will be removed from the cycle.`}
                confirmLabel="Delete"
                destructive
                onConfirm={() => onDelete && onDelete(deleteBug.id)}
                onClose={() => setDeleteBug(null)}
            />
        </>
    );
};

export default TesterBugsList;
